import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Loader2, ShieldCheck, FileUp, ClipboardCheck, UserCheck, Eye, Lock,
  CheckCircle2, ChevronDown, ChevronRight, Info, AlertTriangle, Save, RefreshCw,
} from 'lucide-react';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const SECTION_META = {
  admin_can_record: {
    title: 'Admin can record',
    subtitle: 'Genuine existing external evidence an authorised Osabea user may record now. Recording never verifies.',
    icon: ClipboardCheck,
    accent: 'emerald',
  },
  osabea_must_review: {
    title: 'Osabea must review',
    subtitle: 'Evidence is present but verification/approval remains an employer responsibility.',
    icon: Eye,
    accent: 'amber',
  },
  worker_must_complete: {
    title: 'Worker must complete',
    subtitle: 'Personal declarations, signatures, consent or referee responses. Admin can NEVER do these on the worker’s behalf.',
    icon: UserCheck,
    accent: 'sky',
  },
  already_evidenced: {
    title: 'Already present',
    subtitle: 'Valid canonical evidence already on file — nothing to re-enter.',
    icon: CheckCircle2,
    accent: 'slate',
  },
};

const ACCENT = {
  emerald: { ring: 'border-emerald-200', head: 'bg-emerald-50 text-emerald-900', dot: 'bg-emerald-500', chip: 'bg-emerald-100 text-emerald-800' },
  amber: { ring: 'border-amber-200', head: 'bg-amber-50 text-amber-900', dot: 'bg-amber-500', chip: 'bg-amber-100 text-amber-800' },
  sky: { ring: 'border-sky-200', head: 'bg-sky-50 text-sky-900', dot: 'bg-sky-500', chip: 'bg-sky-100 text-sky-800' },
  slate: { ring: 'border-slate-200', head: 'bg-slate-50 text-slate-700', dot: 'bg-slate-400', chip: 'bg-slate-100 text-slate-700' },
};

function SummaryStat({ label, value, tone }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${tone}`}>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="mt-1 text-xs font-medium opacity-80">{label}</div>
    </div>
  );
}

function RecordEvidenceForm({ employeeId, item, token, onDone }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ original_issue_date: '', expiry_date: '', evidence_reference: '', original_checker_name: '', note: '' });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!file) { toast.error('Attach the existing document you already hold.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('requirement_id', item.requirement_key);
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      fd.append('source', 'existing_external_evidence');
      const { data } = await axios.post(
        `${API}/assisted-onboarding/${employeeId}/record-evidence`, fd,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Existing evidence recorded — awaiting Osabea verification.');
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record evidence.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
        <FileUp className="h-4 w-4" /> Record existing document evidence
      </div>
      <div>
        <Label className="text-xs">Existing document file</Label>
        <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-white" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Original issue / completion date</Label>
          <Input type="date" value={form.original_issue_date} onChange={(e) => setForm({ ...form, original_issue_date: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Expiry date (if applicable)</Label>
          <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Evidence / document reference</Label>
          <Input value={form.evidence_reference} onChange={(e) => setForm({ ...form, evidence_reference: e.target.value })} placeholder="e.g. cert no." />
        </div>
        <div>
          <Label className="text-xs">Originally checked by (if known)</Label>
          <Input value={form.original_checker_name} onChange={(e) => setForm({ ...form, original_checker_name: e.target.value })} placeholder="Name / pre-CareOps" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Note</Label>
        <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="How/where this evidence is held" />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-emerald-800">Recorded as an imported existing record. This does not verify it.</span>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={submit}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Record evidence'}
        </Button>
      </div>
    </div>
  );
}

function RecordCheckForm({ employeeId, item, token, onDone }) {
  const [form, setForm] = useState({ method: '', checked_at: '', outcome: '', original_checker_name: '', note: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.method || !form.checked_at || !form.outcome) {
      toast.error('A real historical check needs method, date and outcome.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/assisted-onboarding/${employeeId}/record-check`,
        { requirement_id: item.requirement_key, source: 'existing_external_evidence', ...form },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Historical employer check recorded. Overall readiness is recalculated by CareOps.');
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record check.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-emerald-300 bg-white p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
        <ShieldCheck className="h-4 w-4" /> Record a genuine historical employer check
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Method actually used</Label>
          <Input value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} placeholder="e.g. home_office_online_check" />
        </div>
        <div>
          <Label className="text-xs">Date the real check occurred</Label>
          <Input type="date" value={form.checked_at} onChange={(e) => setForm({ ...form, checked_at: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Real outcome</Label>
          <Input value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} placeholder="e.g. verified / cleared" />
        </div>
        <div>
          <Label className="text-xs">Originally checked by (if known)</Label>
          <Input value={form.original_checker_name} onChange={(e) => setForm({ ...form, original_checker_name: e.target.value })} />
        </div>
      </div>
      <div>
        <Label className="text-xs">Note</Label>
        <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-emerald-800">Only record checks that genuinely occurred. An employer check is itself a verification activity — its canonical state reflects the real outcome. Overall work readiness is still recalculated by CareOps from all requirements.</span>
        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" disabled={saving} onClick={submit}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Record check'}
        </Button>
      </div>
    </div>
  );
}

function RequirementRow({ employeeId, item, section, token, onRecorded }) {
  const [open, setOpen] = useState(false);
  const action = item.assisted_action || {};
  const supported = action.supported;
  const canEvidence = (action.action_types || []).includes('record_evidence');
  const canCheck = (action.action_types || []).includes('record_check');

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => (supported ? setOpen((o) => !o) : null)}
        className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left ${supported ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default'}`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900">{item.name}</span>
            {item.verification_status && (
              <Badge variant="outline" className="text-[10px]">{String(item.verification_status).replace(/_/g, ' ')}</Badge>
            )}
            {action.worker_owned && <Badge className="bg-sky-100 text-sky-800 text-[10px]"><Lock className="mr-1 h-3 w-3" />worker-owned</Badge>}
            {action.manual_follow_up && <Badge className="bg-slate-100 text-slate-600 text-[10px]">manual follow-up</Badge>}
          </div>
          <p className="mt-1 text-xs text-slate-500">{item.rationale}</p>
          {!supported && action.reason_unsupported && (
            <p className="mt-1 flex items-start gap-1 text-[11px] text-slate-500"><Info className="mt-0.5 h-3 w-3 shrink-0" />{action.reason_unsupported}</p>
          )}
        </div>
        {supported && (
          <span className="shrink-0 text-slate-400">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
        )}
      </button>

      {open && supported && (
        <div className="border-t border-slate-100 px-4 pb-4 pt-2">
          {canEvidence && (
            <RecordEvidenceForm employeeId={employeeId} item={item} token={token} onDone={() => { setOpen(false); onRecorded(); }} />
          )}
          {canCheck && (
            <RecordCheckForm employeeId={employeeId} item={item} token={token} onDone={() => { setOpen(false); onRecorded(); }} />
          )}
        </div>
      )}
    </div>
  );
}

function Section({ sectionKey, items, employeeId, token, onRecorded }) {
  const meta = SECTION_META[sectionKey];
  const accent = ACCENT[meta.accent];
  const [collapsed, setCollapsed] = useState(sectionKey === 'already_evidenced');
  const Icon = meta.icon;

  return (
    <div className={`overflow-hidden rounded-xl border ${accent.ring}`}>
      <button type="button" onClick={() => setCollapsed((c) => !c)}
        className={`flex w-full items-center justify-between gap-3 px-4 py-3 ${accent.head}`}>
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5" />
          <span className="font-semibold">{meta.title}</span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${accent.chip}`}>{items.length}</span>
        </div>
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {!collapsed && (
        <div className="space-y-2 bg-white p-3">
          <p className="px-1 text-xs text-slate-500">{meta.subtitle}</p>
          {items.length === 0 && <p className="px-1 py-2 text-sm text-slate-400">Nothing here.</p>}
          {items.map((item) => (
            <RequirementRow key={`${sectionKey}-${item.requirement_key}`} employeeId={employeeId}
              item={item} section={sectionKey} token={token} onRecorded={onRecorded} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AssistedOnboardingWorkspace({ employeeId, open, onClose }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState('');
  const [savingSession, setSavingSession] = useState(false);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/assisted-onboarding/${employeeId}/workspace`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res);
      setNotes(res?.session?.notes || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load assisted onboarding.');
    } finally {
      setLoading(false);
    }
  }, [employeeId, token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const saveSession = async () => {
    setSavingSession(true);
    try {
      await axios.put(`${API}/assisted-onboarding/${employeeId}/session`,
        { notes }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Progress saved — you can resume later.');
    } catch (e) {
      toast.error('Could not save progress.');
    } finally {
      setSavingSession(false);
    }
  };

  const buckets = data?.buckets || {};
  const summary = data?.summary || {};
  const ready = data?.canonical_readiness || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            Record existing onboarding evidence
          </DialogTitle>
          <DialogDescription>
            {data?.employee ? (
              <>For <span className="font-medium text-slate-700">{data.employee.first_name} {data.employee.last_name}</span> · {data.employee.role}</>
            ) : 'Assisted onboarding'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex h-48 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !data ? (
          <div className="py-10 text-center text-slate-400">No data.</div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Progress summary
                <span className="ml-auto flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">
                  {ready.is_work_ready ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <AlertTriangle className="h-3 w-3 text-amber-500" />}
                  {ready.is_work_ready ? 'Work-ready (canonical)' : 'Not yet work-ready'}
                  {ready.progress ? ` · ${ready.progress.percentage ?? 0}%` : ''}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <SummaryStat label="Recorded this session" value={summary.recorded_this_session ?? 0} tone="border-emerald-200 bg-emerald-50 text-emerald-900" />
                <SummaryStat label="Already present" value={summary.already_present ?? 0} tone="border-slate-200 bg-white text-slate-700" />
                <SummaryStat label="Admin can record" value={summary.admin_can_record ?? 0} tone="border-emerald-200 bg-white text-emerald-800" />
                <SummaryStat label="Worker actions remaining" value={summary.worker_actions_remaining ?? 0} tone="border-sky-200 bg-sky-50 text-sky-900" />
                <SummaryStat label="Osabea review remaining" value={summary.osabea_review_remaining ?? 0} tone="border-amber-200 bg-amber-50 text-amber-900" />
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                Canonical readiness is calculated by the Unified Compliance Engine. Recording existing evidence never marks anyone work-ready.
              </p>
            </div>

            {['admin_can_record', 'osabea_must_review', 'worker_must_complete', 'already_evidenced'].map((key) => (
              <Section key={key} sectionKey={key} items={buckets[key] || []}
                employeeId={employeeId} token={token} onRecorded={load} />
            ))}

            <div className="rounded-xl border border-slate-200 p-3">
              <Label className="text-xs font-semibold text-slate-600">Handover note (saved for resume)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="What is left, who to chase, anything the next admin should know…" className="mt-1" />
              <div className="mt-3 flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" onClick={load}><RefreshCw className="mr-1 h-4 w-4" /> Refresh</Button>
                <Button size="sm" onClick={saveSession} disabled={savingSession}>
                  {savingSession ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save & resume later</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
